import { useState, useEffect, useCallback } from 'react';
import { schoolApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Trash2, Loader2, User } from 'lucide-react';
import { toast } from 'sonner';

export default function SchoolTeachersPage() {
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ first_name: '', last_name: '', phone: '', email: '', teacher_type: 'quranic' });

  const loadData = useCallback(async () => {
    try {
      const res = await schoolApi.getTeachers();
      setTeachers(res.data);
    } catch { toast.error('Erreur'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await schoolApi.createTeacher(form);
      toast.success('Enseignant ajoute');
      setDialogOpen(false);
      setForm({ first_name: '', last_name: '', phone: '', email: '', teacher_type: 'quranic' });
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Supprimer ${name} ?`)) return;
    try { await schoolApi.deleteTeacher(id); toast.success('Supprime'); loadData(); } catch { toast.error('Erreur'); }
  };

  return (
    <div className="space-y-6" data-testid="teachers-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-stone-900">Enseignants</h1>
          <p className="text-sm text-stone-500">{teachers.length} enseignant(s)</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-700 hover:bg-emerald-800" data-testid="add-teacher-btn"><Plus className="w-4 h-4 mr-1" /> Ajouter</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Nouvel enseignant</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Prenom *</Label><Input value={form.first_name} onChange={set('first_name')} required data-testid="teacher-firstname" /></div>
                <div><Label>Nom *</Label><Input value={form.last_name} onChange={set('last_name')} required data-testid="teacher-lastname" /></div>
              </div>
              <div><Label>Email</Label><Input type="email" value={form.email} onChange={set('email')} data-testid="teacher-email" /></div>
              <div><Label>Telephone</Label><Input value={form.phone} onChange={set('phone')} data-testid="teacher-phone" /></div>
              <Button type="submit" className="w-full bg-emerald-700 hover:bg-emerald-800" data-testid="teacher-submit">Ajouter</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-stone-400" /></div>
      ) : teachers.length === 0 ? (
        <Card><CardContent className="p-12 text-center text-stone-400">Aucun enseignant</CardContent></Card>
      ) : (
        <Card className="border-stone-200">
          <div className="divide-y divide-stone-100">
            {teachers.map(t => (
              <div key={t.id} className="p-3 flex items-center justify-between hover:bg-stone-50" data-testid={`teacher-row-${t.id}`}>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center"><User className="w-4 h-4 text-blue-600" /></div>
                  <div>
                    <p className="text-sm font-medium text-stone-900">{t.first_name} {t.last_name}</p>
                    <p className="text-xs text-stone-400">{t.email || t.phone || '-'}</p>
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => handleDelete(t.id, `${t.first_name} ${t.last_name}`)} className="h-7 w-7 p-0 text-red-400" data-testid={`delete-teacher-${t.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
